import { memo, useMemo, Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
import { generateId } from '@agilepoint/utils-common';
import { useTranslate } from '@agilepoint/wat-react';
import { StackPanel, Typography } from '@agilepoint/wat-react-ux';
import { ElasticTextBoxField, urlRegExp } from 'common';
import { AuthenticationFormWrapper, SectionLabel, AuthFieldWrapper, StyledCheckboxField } from '../styled-components';
import { AuthenticationProviderKeys } from '../types';

interface IAuthProviderDetailsProps {
    authProviderLabel: string;
    showDetails?: boolean;
    authProviderFieldDetails: {
        fieldName?: string;
        fieldLabel?: string;
        required?: boolean | string;
        renderCustomField?: () => React.ReactNode;
    }[];
}

export const SelectedAuthContentForm = () => {
    const { watch } = useFormContext();
    const selectedAuthProviders: string[] = watch('authentication');
    const translate = useTranslate();

    const providers = useMemo(() => selectedAuthProviders.reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
    }, {} as { [key in AuthenticationProviderKeys]: boolean }), [selectedAuthProviders]);

    const authProviderDetails = useMemo(() => [{
        showDetails: providers.activeDirectoryConfiguration,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.adConfigLabel'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.domainFieldLabel'), fieldName: `${AuthenticationProviderKeys.ActiveDirectory}.domain`, required: 'Domain Name is required' },
            {
                renderCustomField: () => <StyledCheckboxField name={`${AuthenticationProviderKeys.ActiveDirectory}.enableJWT`} title={translate('modules.createNewApp.steps.authentication.fieldLabel.enableJWT')} label={translate('modules.createNewApp.steps.authentication.fieldLabel.enableJWT')} />
            },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.audienceURL'), fieldName: `${AuthenticationProviderKeys.ActiveDirectory}.audienceURL` }
        ]
    },
    {
        showDetails: providers.identityServerConfiguration,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.idServerConfigLabel'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.clientidFieldLabel'), fieldName: `${AuthenticationProviderKeys.IdentityServer}.clientId` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.authorityFieldLabel'), fieldName: `${AuthenticationProviderKeys.IdentityServer}.authority` }
        ]
    },
    {
        showDetails: providers.googleConfiguration,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.googleConfigLabel'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.clientidFieldLabel'), fieldName: `${AuthenticationProviderKeys.GoogleConfiguration}.clientId` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.resourceLabel'), fieldName: `${AuthenticationProviderKeys.GoogleConfiguration}.resource` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.redirectURLLabel'), fieldName: `${AuthenticationProviderKeys.GoogleConfiguration}.returnURL` }
        ]
    }, {
        showDetails: providers.oktaConfiguration,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.oktaServerConfigLabel'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.clientidFieldLabel'), fieldName: `${AuthenticationProviderKeys.Okta}.clientId` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.authorityFieldLabel'), fieldName: `${AuthenticationProviderKeys.Okta}.authority` }
        ]
    },
    {
        showDetails: providers.salesforceConfiguration,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.salesforceConfigLabel'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.clientidFieldLabel'), fieldName: `${AuthenticationProviderKeys.Salesforce}.consumerKey` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.redirectURLFieldLabel'), fieldName: `${AuthenticationProviderKeys.Salesforce}.consumerSecret` }
        ]
    },
    {
        showDetails: providers.officeConfiguration,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.msaaDirectoryConfigLabel'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.clientidFieldLabel'), fieldName: `${AuthenticationProviderKeys.MSAzureActiveDirectory}.clientId` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.returnUrlFieldLabel'), fieldName: `${AuthenticationProviderKeys.MSAzureActiveDirectory}.returnURL` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.resourceFieldLabel'), fieldName: `${AuthenticationProviderKeys.MSAzureActiveDirectory}.resource` },
            {
                renderCustomField: () => (<StyledCheckboxField
                    name={`${AuthenticationProviderKeys.MSAzureActiveDirectory}.useMSAuthenticatorApp`}
                    label={translate('modules.createNewApp.steps.authentication.fieldLabel.useMSAuthenticatorApp')}
                    title={translate('modules.createNewApp.steps.authentication.fieldLabel.useMSAuthenticatorApp')} />)
            }
        ]
    },
    {
        showDetails: providers.officeConfigurationChina,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.msaadChinaConfigLabel'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.clientidFieldLabel'), fieldName: `${AuthenticationProviderKeys.MSAzureActiveDirectoryChina}.clientId` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.returnUrlFieldLabel'), fieldName: `${AuthenticationProviderKeys.MSAzureActiveDirectoryChina}.returnURL` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.resourceFieldLabel'), fieldName: `${AuthenticationProviderKeys.MSAzureActiveDirectoryChina}.resource` },
            {
                renderCustomField: () => (<StyledCheckboxField
                    name={`${AuthenticationProviderKeys.MSAzureActiveDirectoryChina}.useMSAuthenticatorApp`}
                    label={translate('modules.createNewApp.steps.authentication.fieldLabel.useMSAuthenticatorApp')}
                    title={translate('modules.createNewApp.steps.authentication.fieldLabel.useMSAuthenticatorApp')} />)
            }
        ]
    },
    {
        showDetails: providers.pingFederateConfiguration,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.pingFederate'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.clientidFieldLabel'), fieldName: `${AuthenticationProviderKeys.PingFederate}.clientId` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.authorityIssuerURL'), fieldName: `${AuthenticationProviderKeys.PingFederate}.authority` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.userClaimType'), fieldName: `${AuthenticationProviderKeys.PingFederate}.usernameClaimType` }
        ]
    },
    {
        showDetails: providers.amazonCognitoConfiguration,
        authProviderLabel: translate('modules.createNewApp.steps.authentication.authProviderLabel.amazonCognito'),
        authProviderFieldDetails: [
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.clientidFieldLabel'), fieldName: `${AuthenticationProviderKeys.AmazonCognito}.clientId` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.authorityIssuerURL'), fieldName: `${AuthenticationProviderKeys.AmazonCognito}.authority` },
            { fieldLabel: translate('modules.createNewApp.steps.authentication.fieldLabel.userImpersonationScopeURL'), fieldName: `${AuthenticationProviderKeys.AmazonCognito}.userImpersonationScopeURL` }
        ]
    }], [providers.activeDirectoryConfiguration, providers.amazonCognitoConfiguration, providers.googleConfiguration,
    providers.identityServerConfiguration, providers.officeConfiguration,
    providers.officeConfigurationChina, providers.oktaConfiguration, providers.pingFederateConfiguration, providers.salesforceConfiguration, translate]);

    return (
        <AuthenticationFormWrapper width="660px" orientation='vertical' gap="30">
            <ElasticTextBoxField
                name="serverInstanceURL"
                title={translate('modules.createNewApp.steps.authentication.fieldLabel.serverInstanceURL')}
                label={translate('modules.createNewApp.steps.authentication.fieldLabel.serverInstanceURL')}
                placeholder='https://serverinstanceurl/agilepointserver'
                validationRules={{
                    pattern: { value: urlRegExp, message: translate('common.messages.validSiteUriText') }
                }} />
            <>{authProviderDetails.map((item) => <AuthProviderDetails key={item.authProviderLabel} {...item} />)}</>
        </AuthenticationFormWrapper>
    );
};

const AuthProviderDetails = memo((props: IAuthProviderDetailsProps) => {
    const { authProviderLabel, authProviderFieldDetails, showDetails = false } = props;
    return (
        <>
            {
                showDetails &&
                <StackPanel gap="20" orientation='vertical' width='100%'>
                    <SectionLabel><Typography.H6 title={authProviderLabel}>{authProviderLabel.toLocaleUpperCase()}</Typography.H6></SectionLabel>
                    <AuthFieldWrapper width="100%">
                        {authProviderFieldDetails.map(({ fieldLabel, fieldName, required, renderCustomField }) => (
                            <Fragment key={fieldName ?? generateId()}>
                                {renderCustomField ? renderCustomField()
                                    : <ElasticTextBoxField
                                        $width="calc(50% - 10px)"
                                        $flexGrow='1'
                                        name={fieldName}
                                        title={fieldLabel}
                                        label={fieldLabel}
                                        validationRules={{ required: required }} />}
                            </Fragment>
                        ))}
                    </AuthFieldWrapper>
                </StackPanel>
            }
        </>
    );
});
